import { useState, useLayoutEffect, useEffect } from 'react'

import { GatewaysGatewayIdDeleteRequest, OriginGatewayPaginatedList } from '@nativewaves/nw-cloud-sdk/origin'
import { useQuery, useInfiniteQuery, useMutationState, InfiniteData, useQueryClient } from '@tanstack/react-query'
import { VirtualItem } from '@tanstack/react-virtual'

import { gatewayMutationKeys } from 'hooks/mutations/origin'
import { originGatewaysOverviewRoute } from 'pages/NWPlatform/OriginGateways'
import { gatewayQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'
import { mergeBatchIntoSource } from 'utils'

type UseAsyncContentCaching = {
  virtualItems?: VirtualItem[]
  setLoadingRows: (rows: string[]) => void
}

export const useAsyncContentCaching = ({ virtualItems, setLoadingRows }: UseAsyncContentCaching) => {
  const queryClient = useQueryClient()

  const { environmentId } = originGatewaysOverviewRoute.useParams()
  const search = originGatewaysOverviewRoute.useSearch()
  const { query } = search

  const gatewaysQuery = useInfiniteQuery({
    ...gatewayQueryKeys.list({ environmentId, query }),
    select: (data) => ({
      ...data,
      flat: data?.pages.flatMap((page) => page.items),
    }),
    staleTime: Infinity,
    notifyOnChangeProps: 'all',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const [batchGetEnabled, setBatchGetEnabled] = useState(gatewaysQuery.isSuccess)

  const pendingGateways = useMutationState({
    filters: { mutationKey: gatewayMutationKeys.delete(), status: 'pending' },
    select: (mutation) => mutation.state.variables as GatewaysGatewayIdDeleteRequest,
  })

  const gatewayBatchQuery = useQuery({
    ...gatewayQueryKeys.batchGet(
      {},
      () => virtualItems?.map((virtualItem) => gatewaysQuery.data?.flat[virtualItem.index]?.id) as string[],
    ),
    enabled: batchGetEnabled,
    refetchInterval: RefetchIntervals.NORMAL,
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useLayoutEffect(() => {
    if (gatewaysQuery.isSuccess && gatewayBatchQuery.isSuccess) {
      queryClient.setQueriesData<InfiniteData<OriginGatewayPaginatedList>>(
        { queryKey: gatewayQueryKeys.lists() },
        (old) => {
          if (old) {
            return {
              ...old,
              pages:
                old.pages.map((page) => ({
                  ...page,
                  items: mergeBatchIntoSource(
                    page.items,
                    gatewayBatchQuery.data.items,
                    new Set(gatewayBatchQuery.data.notFoundIds),
                  ),
                })) ?? [],
            }
          }
          return old
        },
      )
    }
  }, [
    gatewayBatchQuery.data?.items,
    gatewayBatchQuery.data?.notFoundIds,
    gatewayBatchQuery.isSuccess,
    gatewaysQuery.isSuccess,
    queryClient,
  ])

  useLayoutEffect(() => {
    setLoadingRows([...new Set([...pendingGateways.map((task) => task.gatewayId)])])
  }, [pendingGateways, setLoadingRows])

  useEffect(() => {
    if (gatewaysQuery.isSuccess) {
      if (!batchGetEnabled) {
        const timeoutHandle = setTimeout(() => {
          setBatchGetEnabled(true)
          clearTimeout(timeoutHandle)
        }, 5000)
      }
    }
  }, [batchGetEnabled, gatewaysQuery.isSuccess])

  return {
    gatewaysQuery,
    setBatchGetEnabled,
  }
}
